import Attribution from "../../../geo/Attribution";
import {useGeoLookup} from '../../../geo/useGeoLookup';
import GeoRetry from '../../../geo/GeoRetry';
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Spin, message, Card, Row, Col, Statistic, Divider, Switch } from 'antd';
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  LineChartOutlined,
  CarOutlined,
  SwapRightOutlined
} from '@ant-design/icons';
import { MapCanvas as GoogleMap, MapPolyline as Polyline, MapMarker as Marker, RouteLayer as DirectionsRenderer, MapInfoWindow as InfoWindow, useMapLoader, fitMapBounds, circleSymbol } from "../../../geo/Map";
import { routeEndpoints } from "../../../geo/service";
import { formatLocationAddress } from "../../../geo/addressLabel";
import api from '../../../axiosConfig';

const mapContainerStyle = {
  width: '100%',
  height: '500px',
  borderRadius: '8px'
};

const mapOptions = {
  disableDefaultUI: false,
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: true,
  fullscreenControl: true,
};

// Iconos personalizados para marcadores
const iconoInicio = {
  path: circleSymbol(),
  fillColor: '#52c41a',
  fillOpacity: 1,
  strokeColor: '#fff',
  strokeWeight: 2,
  scale: 10,
};

const iconoFin = {
  path: circleSymbol(),
  fillColor: '#ff4d4f',
  fillOpacity: 1,
  strokeColor: '#fff',
  strokeWeight: 2,
  scale: 10,
};

const RutaTrazadaView = ({ visible, trasladoId, onClose, traslado }) => {
  const [loading, setLoading] = useState(false);
  const [rutaData, setRutaData] = useState(null);
  const [center, setCenter] = useState({ lat: 4.6097, lng: -74.0817 }); // Bogotá por defecto
  const [usarCalles, setUsarCalles] = useState(true);
  const [map, setMap] = useState(null);
  const [marcadorAbierto, setMarcadorAbierto] = useState(null);

  const { isLoaded, loadError, version } = useMapLoader();
  const lookupOptions={enabled:isLoaded && !!rutaData && visible,version};
  const routeLookup=useGeoLookup('route',routeEndpoints(rutaData),{...lookupOptions,enabled:lookupOptions.enabled && usarCalles});
  const startLookup=useGeoLookup('reverse',rutaData?.punto_inicio?{lat:Number(rutaData.punto_inicio.lat),lng:Number(rutaData.punto_inicio.lng)}:null,{...lookupOptions,endpoint:'inicio'});
  const endLookup=useGeoLookup('reverse',rutaData?.punto_fin?{lat:Number(rutaData.punto_fin.lat),lng:Number(rutaData.punto_fin.lng)}:null,{...lookupOptions,endpoint:'fin'});
  const directionsResponse=routeLookup.data;
  const addressLookups={inicio:startLookup,fin:endLookup};
  const direcciones=Object.fromEntries(Object.entries(addressLookups).flatMap(([name,lookup])=>[[name,formatLocationAddress(lookup.data?.result)||(lookup.busy?'Buscando dirección...':'Dirección no disponible')],[`${name}Attribution`,lookup.data?.result?.attribution]]));


  useEffect(() => {
    let active=true;
    setRutaData(null);setMarcadorAbierto(null);
    if (visible && trasladoId) fetchRutaData(()=>active);
    return ()=>{active=false;};
  }, [visible, trasladoId]);

  const fetchRutaData = async (active) => {
    setLoading(true);
    try {
      const response = await api.get(`/gps/ruta/${trasladoId}`);

      if(!active())return;
      if (response.data.success) {
        const data = response.data.data;
        setRutaData(data);

        // Centrar mapa en el primer punto si existe
        if (data.ruta && data.ruta.length > 0) {
          const primerPunto = data.ruta[0];
          if (typeof primerPunto.lat === 'number' && typeof primerPunto.lng === 'number') {
            setCenter({ lat: primerPunto.lat, lng: primerPunto.lng });
          }
        } else if (data.punto_inicio &&
                   typeof data.punto_inicio.lat === 'number' &&
                   typeof data.punto_inicio.lng === 'number') {
          setCenter({ lat: data.punto_inicio.lat, lng: data.punto_inicio.lng });
        }
      }
    } catch (error) {
      if(!active())return;
      console.error('Error al cargar la ruta:', error);
      message.error('No se pudo cargar la ruta del traslado');
    } finally {
      if(active())setLoading(false);
    }
  };

  const formatDuracion = (segundos) => {
    if (!segundos) return 'N/A';
    const horas = Math.floor(segundos / 3600);
    const minutos = Math.floor((segundos % 3600) / 60);
    const segs = segundos % 60;

    if (horas > 0) {
      return `${horas}h ${minutos}m ${segs}s`;
    } else if (minutos > 0) {
      return `${minutos}m ${segs}s`;
    } else {
      return `${segs}s`;
    }
  };

  const formatFecha = (isoString) => {
    if (!isoString) return 'N/A';
    const fecha = new Date(isoString);
    return fecha.toLocaleString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Obtener estadísticas
  const getEstadisticas = () => {
    if (!rutaData) return null;

    const totalPuntos = rutaData.total_puntos || 0;
    const distanciaKm = rutaData.distancia_recorrida_km || 0;

    // Calcular duración si hay punto_inicio y punto_fin
    let duracionSegundos = null;
    if (rutaData.punto_inicio && rutaData.punto_fin) {
      const inicio = new Date(rutaData.punto_inicio.timestamp);
      const fin = new Date(rutaData.punto_fin.timestamp);
      duracionSegundos = Math.floor((fin - inicio) / 1000);
    }

    // Calcular velocidad promedio aproximada (km/h)
    let velocidadPromedio = null;
    if (distanciaKm > 0 && duracionSegundos > 0) {
      const duracionHoras = duracionSegundos / 3600;
      velocidadPromedio = (distanciaKm / duracionHoras).toFixed(2);
    }

    return {
      totalPuntos,
      distanciaKm,
      duracionSegundos,
      velocidadPromedio,
    };
  };

  const stats = getEstadisticas();

  // Preparar datos para la polilínea (modo puntos GPS directos)
  const rutaPath = rutaData?.ruta?.map(punto => ({
    lat: punto.lat,
    lng: punto.lng
  })) || [];

  const polylineOptions = {
    strokeColor: '#1890ff',
    strokeOpacity: 0.8,
    strokeWeight: 4,
    geodesic: true,
  };

  const directionsRendererOptions = {
    // Los marcadores I/F propios muestran la descripción del punto (llegada sin iniciar, fallido, sin firma)
    suppressMarkers: true,
    polylineOptions: {
      strokeColor: '#1890ff',
      strokeOpacity: 0.8,
      strokeWeight: 5,
    }
  };

  const onMapLoad = useCallback((map) => {
    setMap(map);
  }, []);

  // Sin ruta por calles: encuadrar los puntos GPS (con un solo punto, centrar con zoom cercano)
  useEffect(() => {
    if (!map || !rutaData || (usarCalles && directionsResponse)) return;
    const puntos = [
      ...(rutaData.ruta || []),
      ...['punto_inicio', 'punto_fin'].map(k => rutaData[k]).filter(Boolean),
    ].filter(p => typeof p.lat === 'number' && typeof p.lng === 'number');
    if (puntos.length === 1) {
      map.setCenter({ lat: puntos[0].lat, lng: puntos[0].lng });
      map.setZoom(16);
    } else if (puntos.length > 1) {
      fitMapBounds(map, puntos);
    }
  }, [map, rutaData, usarCalles, directionsResponse]);

  if (!isLoaded) {
    return (
      <Modal
        title="Ruta Realizada"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={900}
        centered
      >
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 20 }}>{loadError?.message || "Cargando mapa..."}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <EnvironmentOutlined />
          <span>Ruta GPS Realizada - Traslado #{trasladoId}</span>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={950}
      centered
      bodyStyle={{ padding: '24px' }}
    >
      <GeoRetry lookup={routeLookup} label="Reintentar ruta" />
      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: 20 }}>Cargando datos de la ruta...</p>
        </div>
      ) : rutaData ? (
        <>
          {/* Información de Origen y Destino */}
          {traslado && (
            <Card
              size="small"
              style={{ marginBottom: 16, backgroundColor: '#f6f8fa' }}
            >
              <Row gutter={16} align="middle">
                <Col xs={24} sm={11}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <EnvironmentOutlined style={{ color: '#52c41a', fontSize: 18, marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#52c41a', fontSize: 12 }}>ORIGEN</div>
                      <div style={{ fontWeight: 500 }}>
                        {traslado.origen_ciudad || 'N/A'}, {traslado.origen_departamento || ''}
                      </div>
                      {traslado.origen_direccion && (
                        <div style={{ fontSize: 12, color: '#666' }}>{traslado.origen_direccion}</div>
                      )}
                    </div>
                  </div>
                </Col>
                <Col xs={24} sm={2} style={{ textAlign: 'center' }}>
                  <SwapRightOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                </Col>
                <Col xs={24} sm={11}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <EnvironmentOutlined style={{ color: '#ff4d4f', fontSize: 18, marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 600, color: '#ff4d4f', fontSize: 12 }}>DESTINO</div>
                      <div style={{ fontWeight: 500 }}>
                        {traslado.destino_ciudad || 'N/A'}, {traslado.destino_departamento || ''}
                      </div>
                      {traslado.destino_direccion && (
                        <div style={{ fontSize: 12, color: '#666' }}>{traslado.destino_direccion}</div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {/* Estadísticas */}
          {stats && (
            <>
              <Row gutter={16} style={{ marginBottom: 20 }}>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="Puntos GPS"
                      value={stats.totalPuntos}
                      prefix={<EnvironmentOutlined />}
                      valueStyle={{ color: '#1890ff', fontSize: '1.5rem' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="Distancia"
                      value={stats.distanciaKm}
                      suffix="km"
                      prefix={<LineChartOutlined />}
                      precision={2}
                      valueStyle={{ color: '#52c41a', fontSize: '1.5rem' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="Duración"
                      value={stats.duracionSegundos ? formatDuracion(stats.duracionSegundos) : 'N/A'}
                      prefix={<ClockCircleOutlined />}
                      valueStyle={{ color: '#faad14', fontSize: '1.5rem' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card>
                    <Statistic
                      title="Vel. Promedio"
                      value={stats.velocidadPromedio || 'N/A'}
                      suffix={stats.velocidadPromedio ? "km/h" : ""}
                      prefix={<CarOutlined />}
                      valueStyle={{ color: '#722ed1', fontSize: '1.5rem' }}
                    />
                  </Card>
                </Col>
              </Row>

              {/* Control de visualización */}
              {rutaPath.length >= 2 && (
                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                  <Switch
                    checked={usarCalles}
                    onChange={setUsarCalles}
                    checkedChildren="Ruta por calles"
                    unCheckedChildren="Puntos GPS directos"
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
                    {usarCalles && directionsResponse ? 'Mostrando ruta siguiendo las calles' : 'Mostrando puntos GPS conectados directamente'}
                  </div>
                </div>
              )}

              <Divider />
            </>
          )}

          {/* Mapa */}
          {rutaPath.length > 0 ? (
            <div style={{ marginTop: 20 }}>
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={center}
                zoom={13}
                options={mapOptions}
                onLoad={onMapLoad}
              >
                {/* Mostrar ruta por calles O puntos GPS directos */}
                {usarCalles && directionsResponse ? (
                  <DirectionsRenderer
                    directions={directionsResponse}
                    options={directionsRendererOptions}
                  />
                ) : (
                  rutaPath.length >= 2 && (
                    /* Polilínea de la ruta (puntos GPS directos) */
                    <Polyline
                      path={rutaPath}
                      options={polylineOptions}
                    />
                  )
                )}

                    {/* Marcador de inicio */}
                    {rutaData.punto_inicio &&
                     typeof rutaData.punto_inicio.lat === 'number' &&
                     typeof rutaData.punto_inicio.lng === 'number' && (
                      <Marker
                        position={{
                          lat: rutaData.punto_inicio.lat,
                          lng: rutaData.punto_inicio.lng
                        }}
                        icon={iconoInicio}
                        title={`${rutaData.punto_inicio.descripcion || 'Inicio'}: ${formatFecha(rutaData.punto_inicio.timestamp)}`}
                        onClick={() => setMarcadorAbierto('inicio')}
                        label={{
                          text: 'I',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}

                    {/* Marcador de fin */}
                    {rutaData.punto_fin &&
                     typeof rutaData.punto_fin.lat === 'number' &&
                     typeof rutaData.punto_fin.lng === 'number' && (
                      <Marker
                        position={{
                          lat: rutaData.punto_fin.lat,
                          lng: rutaData.punto_fin.lng
                        }}
                        icon={iconoFin}
                        title={`${rutaData.punto_fin.descripcion || 'Fin'}: ${formatFecha(rutaData.punto_fin.timestamp)}`}
                        onClick={() => setMarcadorAbierto('fin')}
                        label={{
                          text: 'F',
                          color: 'white',
                          fontWeight: 'bold'
                        }}
                      />
                    )}

                    {/* Al hacer clic en I o F: descripción, hora y dirección del punto */}
                    {['inicio', 'fin']
                      .filter((tipo) => tipo === marcadorAbierto)
                      .map((tipo) => rutaData[`punto_${tipo}`] && (
                        <InfoWindow
                          key={`info-${tipo}`}
                          position={{ lat: rutaData[`punto_${tipo}`].lat, lng: rutaData[`punto_${tipo}`].lng }}
                          onCloseClick={() => setMarcadorAbierto(null)}
                        >
                          <div>
                            <strong>{rutaData[`punto_${tipo}`].descripcion || (tipo === 'inicio' ? 'Inicio' : 'Fin')}</strong>
                            <div>{formatFecha(rutaData[`punto_${tipo}`].timestamp)}</div>
                            <div style={{ marginTop: 4, maxWidth: 260 }}>
                              📍 {direcciones[tipo] || 'Buscando dirección...'}<Attribution attribution={direcciones[`${tipo}Attribution`]} />
                            </div>
                          </div>
                        </InfoWindow>
                      ))}
              </GoogleMap>
              <Attribution attribution={directionsResponse?.attribution} />

              {/* Información adicional */}
              <div style={{ marginTop: 16, fontSize: '12px', color: '#666' }}>
                {rutaData.punto_inicio && (
                  <div>
                    <strong>Inicio:</strong> {formatFecha(rutaData.punto_inicio.timestamp)}
                    {rutaData.punto_inicio.descripcion && ` · ${rutaData.punto_inicio.descripcion}`}
                    <div style={{ marginLeft: 12 }}>📍 {direcciones.inicio || 'Buscando dirección...'}<Attribution attribution={direcciones.inicioAttribution} /></div>
                    <GeoRetry lookup={addressLookups.inicio} label="Reintentar dirección de inicio" />
                  </div>
                )}
                {rutaData.punto_fin && (
                  <div>
                    <strong>Fin:</strong> {formatFecha(rutaData.punto_fin.timestamp)}
                    {rutaData.punto_fin.descripcion && ` · ${rutaData.punto_fin.descripcion}`}
                    <div style={{ marginLeft: 12 }}>📍 {direcciones.fin || 'Buscando dirección...'}<Attribution attribution={direcciones.finAttribution} /></div>
                    <GeoRetry lookup={addressLookups.fin} label="Reintentar dirección de fin" />
                  </div>
                )}
                {stats.totalPuntos < 10 && (
                  <div style={{ color: '#faad14', marginTop: 8 }}>
                    <EnvironmentOutlined /> Pocos puntos GPS registrados ({stats.totalPuntos}). La ruta puede no ser precisa.
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <EnvironmentOutlined style={{ fontSize: 48, color: '#ccc' }} />
              <p style={{ marginTop: 20, color: '#666' }}>
                No hay datos de ruta disponibles para este traslado
              </p>
            </div>
          )}
        </>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p style={{ color: '#666' }}>No se encontraron datos de ruta</p>
        </div>
      )}
    </Modal>
  );
};

export default RutaTrazadaView;
